/**
 * Estimated one-rep-max.
 *
 * A registry rather than a single function, so Brzycki and others can be added
 * later behind a setting without touching the database or any caller. The
 * stored `personal_record` row keeps the resulting value; the formula that
 * produced it is an application setting, and changing it simply recomputes.
 */

export interface OneRepMaxFormula {
  id: string;
  name: string;
  /** Rep count above which the formula stops being meaningful. */
  reliableUpTo: number;
  /** Estimate in grams from a load in grams. Null when not applicable. */
  estimate: (loadG: number, reps: number) => number | null;
}

/** Shared validity gate: positive load, whole reps, within a sane range. */
function isEstimable(loadG: number, reps: number): boolean {
  return (
    Number.isFinite(loadG) &&
    loadG > 0 &&
    Number.isInteger(reps) &&
    reps >= 1 &&
    reps <= MAX_ESTIMABLE_REPS
  );
}

/**
 * Beyond this, every 1RM formula is extrapolating far past its data and the
 * result is noise. Returning null is more honest than a confident wrong number.
 */
export const MAX_ESTIMABLE_REPS = 30;

export const EPLEY: OneRepMaxFormula = {
  id: 'epley',
  name: 'Epley',
  reliableUpTo: 12,
  estimate: (loadG, reps) => {
    if (!isEstimable(loadG, reps)) return null;
    // Epley yields 1.033 × load at one rep, which is wrong by definition:
    // a single rep *is* the one-rep max. Standard practice is to return it.
    if (reps === 1) return Math.round(loadG);
    return Math.round(loadG * (1 + reps / 30));
  },
};

export const BRZYCKI: OneRepMaxFormula = {
  id: 'brzycki',
  name: 'Brzycki',
  reliableUpTo: 10,
  estimate: (loadG, reps) => {
    if (!isEstimable(loadG, reps)) return null;
    // Denominator vanishes at 37 reps; the validity gate already excludes it.
    if (reps >= 37) return null;
    return Math.round((loadG * 36) / (37 - reps));
  },
};

export const LOMBARDI: OneRepMaxFormula = {
  id: 'lombardi',
  name: 'Lombardi',
  reliableUpTo: 15,
  estimate: (loadG, reps) => {
    if (!isEstimable(loadG, reps)) return null;
    return Math.round(loadG * Math.pow(reps, 0.1));
  },
};

export const ONE_REP_MAX_FORMULAS: Readonly<Record<string, OneRepMaxFormula>> = Object.freeze({
  [EPLEY.id]: EPLEY,
  [BRZYCKI.id]: BRZYCKI,
  [LOMBARDI.id]: LOMBARDI,
});

/** Approved default (Phase 0, H1). */
export const DEFAULT_ONE_REP_MAX_FORMULA_ID = 'epley';

export function getOneRepMaxFormula(id: string = DEFAULT_ONE_REP_MAX_FORMULA_ID): OneRepMaxFormula {
  return ONE_REP_MAX_FORMULAS[id] ?? EPLEY;
}

/** Estimate a one-rep max in grams. Null when the inputs cannot support one. */
export function estimateOneRepMaxG(
  loadG: number,
  reps: number,
  formulaId: string = DEFAULT_ONE_REP_MAX_FORMULA_ID,
): number | null {
  return getOneRepMaxFormula(formulaId).estimate(loadG, reps);
}

/** Whether an estimate falls inside the formula's reliable rep range. */
export function isReliableEstimate(
  reps: number,
  formulaId: string = DEFAULT_ONE_REP_MAX_FORMULA_ID,
): boolean {
  return reps >= 1 && reps <= getOneRepMaxFormula(formulaId).reliableUpTo;
}

export function listOneRepMaxFormulas(): OneRepMaxFormula[] {
  return Object.values(ONE_REP_MAX_FORMULAS);
}
